// src/hooks/useAccessibleModules.js
import { useState, useEffect } from 'react';
import settingsService from '../services/SettingsService';
import sessionService from '../services/SessionService';
import userService from '../services/UserService';
import licenseService from '../services/LicenseService';
import entityService from '../services/EntityService';
import { getConfigSync, fetchConfig } from '../api/config';
import { BACKEND_TO_FRONTEND_MODULE_ID } from './useLicense';

// Modul-IDs som alltid är tillgängliga oavsett licens — annars går det inte
// att uppgradera licens eller komma åt grundvyer.
const ALWAYS_ACCESSIBLE = new Set(['dashboard', 'admin']);

const ENFORCING_STATES = new Set(['valid', 'expired_grace', 'expired_locked']);

function backendLicenseFilter() {
  const cfg = getConfigSync();
  if (!cfg || !cfg.license) return null;  // backend onåbar → ingen restriktion
  if (!ENFORCING_STATES.has(cfg.license.state)) return null;  // missing/invalid → ingen restriktion (failsafe)
  const allowed = (cfg.license.modules || [])
    .map((m) => BACKEND_TO_FRONTEND_MODULE_ID[m])
    .filter(Boolean);
  return new Set(allowed);
}

const getAccessibleModuleIds = () => {
  const globalEnabled = settingsService.getEnabledModules();
  const session = sessionService.getSession();
  const activeEntityId = entityService.getActiveEntityId();
  let filtered = globalEnabled;
  if (session?.username) {
    const allowedIds = userService.getUserAllowedModuleIds(session.username);
    if (allowedIds) filtered = filtered.filter(m => allowedIds.includes(m.id));
  }
  // Frontend-licens (per-entity, localStorage) — fortsatt respekterad
  filtered = filtered.filter(m => licenseService.isLicensed(activeEntityId, m.id));

  // Backend-licens (LIC-106) — overlay som ytterligare filtrerar moduler
  // som inte finns i den signerade .lic-filen. Failsafe: om backend är
  // onåbart eller licens saknas → ingen extra restriktion.
  const backendAllowed = backendLicenseFilter();
  if (backendAllowed) {
    filtered = filtered.filter(m => ALWAYS_ACCESSIBLE.has(m.id) || backendAllowed.has(m.id));
  }

  return filtered.map(m => m.id);
};

const getUnlicensedModuleIds = () => {
  const globalEnabled = settingsService.getEnabledModules();
  const session = sessionService.getSession();
  const activeEntityId = entityService.getActiveEntityId();
  let filtered = globalEnabled;
  if (session?.username) {
    const allowedIds = userService.getUserAllowedModuleIds(session.username);
    if (allowedIds) filtered = filtered.filter(m => allowedIds.includes(m.id));
  }
  const backendAllowed = backendLicenseFilter();
  return filtered
    .filter(m => {
      const lockedByLocal = !licenseService.isLicensed(activeEntityId, m.id);
      const lockedByBackend = backendAllowed
        && !ALWAYS_ACCESSIBLE.has(m.id)
        && !backendAllowed.has(m.id);
      return lockedByLocal || lockedByBackend;
    })
    .map(m => m.id);
};

// Returns the list of module IDs accessible to the current user:
// globally enabled + user permissions + licensed for the active entity +
// (NEW) inom backend-licensens modul-lista om enforcing.
const useAccessibleModules = () => {
  const [moduleIds, setModuleIds] = useState(getAccessibleModuleIds);

  useEffect(() => {
    const handleChanged = () => setModuleIds(getAccessibleModuleIds());

    // Särskild hanterare för backend-licens-event: ALLTID force-fetch config
    // först, annars kan vi filtrera mot stale/null cache (fix för demo-17-
    // race där clearConfigCache + sync-event dispatchas i sekvens).
    const handleBackendLicenseChanged = () => {
      fetchConfig({ force: true })
        .then(() => setModuleIds(getAccessibleModuleIds()))
        .catch(() => setModuleIds(getAccessibleModuleIds()));
    };

    // Säkerställ att backend-config är hämtad så filtret kan tillämpas vid mount
    if (!getConfigSync()) {
      fetchConfig().then(handleChanged).catch(() => {});
    }

    window.addEventListener('rms_modules_changed', handleChanged);
    window.addEventListener('rms_licenses_changed', handleChanged);
    window.addEventListener('rms_active_entity_changed', handleChanged);
    window.addEventListener('rms_backend_license_changed', handleBackendLicenseChanged);
    const unsubUser = userService.subscribe(handleChanged);
    const unsubSettings = settingsService.subscribe(handleChanged);
    const unsubLicense = licenseService.subscribe(handleChanged);
    const unsubEntity = entityService.subscribe(handleChanged);
    return () => {
      window.removeEventListener('rms_modules_changed', handleChanged);
      window.removeEventListener('rms_licenses_changed', handleChanged);
      window.removeEventListener('rms_active_entity_changed', handleChanged);
      window.removeEventListener('rms_backend_license_changed', handleBackendLicenseChanged);
      unsubUser();
      unsubSettings();
      unsubLicense();
      unsubEntity();
    };
  }, []);

  return moduleIds;
};

// Returns module IDs that are enabled + user-accessible but blocked by
// missing license (lokal eller backend).
export const useUnlicensedModules = () => {
  const [moduleIds, setModuleIds] = useState(getUnlicensedModuleIds);

  useEffect(() => {
    const handleChanged = () => setModuleIds(getUnlicensedModuleIds());
    const handleBackendLicenseChanged = () => {
      fetchConfig({ force: true })
        .then(() => setModuleIds(getUnlicensedModuleIds()))
        .catch(() => setModuleIds(getUnlicensedModuleIds()));
    };

    if (!getConfigSync()) {
      fetchConfig().then(handleChanged).catch(() => {});
    }

    window.addEventListener('rms_modules_changed', handleChanged);
    window.addEventListener('rms_licenses_changed', handleChanged);
    window.addEventListener('rms_active_entity_changed', handleChanged);
    window.addEventListener('rms_backend_license_changed', handleBackendLicenseChanged);
    const unsubUser = userService.subscribe(handleChanged);
    const unsubSettings = settingsService.subscribe(handleChanged);
    const unsubLicense = licenseService.subscribe(handleChanged);
    const unsubEntity = entityService.subscribe(handleChanged);
    return () => {
      window.removeEventListener('rms_modules_changed', handleChanged);
      window.removeEventListener('rms_licenses_changed', handleChanged);
      window.removeEventListener('rms_active_entity_changed', handleChanged);
      window.removeEventListener('rms_backend_license_changed', handleBackendLicenseChanged);
      unsubUser();
      unsubSettings();
      unsubLicense();
      unsubEntity();
    };
  }, []);

  return moduleIds;
};

export default useAccessibleModules;
